/**
 * Custom hook for invite modal functionality in workspace header
 * 
 * This hook manages the state and handlers for inviting users to a workspace.
 * It handles pending members list, admin toggling, adding members, and
 * integrates with user search functionality.
 * 
 * @module useWorkspaceHeaderInvite
 */

import { useState, useCallback } from 'react';
import { postAddUserToAWorkspace } from '@/apis/core/workspace/post/postAddUserToAWorkspace';
import { deleteUserFromWorkspace } from '@/apis/core/workspace/delete/deleteUserFromWorkspace';
import { patchIsOwner } from '@/apis/core/workspace/patch/patchIsOwner';
import { useWorkspaceErrorHandler } from '@/hooks/useWorkspaceErrorHandler';
import { ApiError } from '@/utils/errors/apiErrorWrapper';
import { UserSearchResult } from '@/apis/core/user/get/searchUsers';
import {
  PendingMember,
  extractErrorResponse,
  normalizeError,
} from '../utils/workspaceHeaderUtils';

/**
 * Parameters for the useWorkspaceHeaderInvite hook
 */
interface UseWorkspaceHeaderInviteParams {
  /** Workspace ID */
  workspaceId: string;
  /** Function to get access token for API calls */
  getAccessToken: () => Promise<string | null>;
  /** Function to refresh workspace data after updates */
  refreshWorkspaces: () => Promise<void>;
  /** Object containing existing workspace user IDs */
  existingUserIds: Record<string, unknown>;
}

/**
 * Return type for the useWorkspaceHeaderInvite hook
 */
interface UseWorkspaceHeaderInviteReturn {
  /** Array of pending members to be added */
  pendingMembers: PendingMember[];
  /** Whether members are currently being added */
  isAddingMembers: boolean;
  /** Whether the invite modal is open */
  isModalOpen: boolean;
  /** Setter for modal open state */
  setIsModalOpen: (open: boolean) => void;
  /** Adds a user to the pending members list */
  addPendingMember: (user: UserSearchResult) => void;
  /** Removes a member from the pending list */
  removePendingMember: (memberId: string) => void;
  /** Toggles admin status for a pending member */
  togglePendingAdmin: (memberId: string) => void;
  /** Adds all pending members to the workspace */
  addAllMembers: () => Promise<void>;
  /** Removes a user from the workspace */
  deleteUser: (userId: string) => Promise<void>;
  /** Makes a user an owner of the workspace (caller must be admin/owner) */
  makeUserOwner: (userId: string) => Promise<void>;
  /** Resets invite modal state */
  resetInviteState: () => void;
}

/**
 * Custom hook for managing workspace invite functionality
 * 
 * This hook provides:
 * - Pending members list management
 * - Owner status toggling for pending members
 * - Adding multiple members to workspace
 * - Removing users from workspace
 * - Proper error handling with toast notifications
 * 
 * @param params - Hook parameters
 * @returns Invite modal state and handlers
 * 
 * @example
 * ```tsx
 * const {
 *   pendingMembers,
 *   isAddingMembers,
 *   addPendingMember,
 *   addAllMembers,
 * } = useWorkspaceHeaderInvite({
 *   workspaceId,
 *   getAccessToken,
 *   refreshWorkspaces,
 *   existingUserIds: workspaceData.workspaceUsers.users,
 * });
 * ```
 */
export const useWorkspaceHeaderInvite = ({
  workspaceId,
  getAccessToken,
  refreshWorkspaces,
  existingUserIds,
}: UseWorkspaceHeaderInviteParams): UseWorkspaceHeaderInviteReturn => {
  // ============= Error Handling =============
  const { handleError, handleSuccess } = useWorkspaceErrorHandler(workspaceId);

  // ============= State =============
  const [pendingMembers, setPendingMembers] = useState<PendingMember[]>([]);
  const [isAddingMembers, setIsAddingMembers] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // ============= Utilities =============

  /**
   * Resets all invite modal state to initial values
   */
  const resetInviteState = useCallback(() => {
    setPendingMembers([]);
    setIsAddingMembers(false);
  }, []);

  /**
   * Checks if a user is already in the workspace or pending list
   * 
   * @param userId - User ID to check
   * @returns True if user is already added or pending
   */
  const isUserAlreadyAdded = useCallback(
    (userId: string): boolean => {
      const isAlreadyPending = pendingMembers.some((m) => m.id === userId);
      const isAlreadyInWorkspace = Object.keys(existingUserIds).includes(userId);
      return isAlreadyPending || isAlreadyInWorkspace;
    },
    [pendingMembers, existingUserIds]
  );

  // ============= Handlers =============

  /**
   * Adds a user to the pending members list
   * 
   * Validates that the user is not already added or pending before adding.
   * 
   * @param user - User search result to add
   */
  const addPendingMember = useCallback(
    (user: UserSearchResult) => {
      if (isUserAlreadyAdded(user.id)) {
        return;
      }

      const newMember: PendingMember = {
        id: user.id,
        firstName: user.firstName,
        lastName: user.lastName,
        username: user.username,
        email: user.username,
        isOwner: false, // Default to non-admin
      };
      setPendingMembers((prev) => [...prev, newMember]);
    },
    [isUserAlreadyAdded]
  );

  /**
   * Removes a member from the pending list
   * 
   * @param memberId - ID of the member to remove
   */
  const removePendingMember = useCallback((memberId: string) => {
    setPendingMembers((prev) => prev.filter((m) => m.id !== memberId));
  }, []);

  /**
   * Toggles admin status for a pending member
   * 
   * @param memberId - ID of the member to toggle
   */
  const togglePendingAdmin = useCallback((memberId: string) => {
    setPendingMembers((prev) =>
      prev.map((m) => (m.id === memberId ? { ...m, isOwner: !m.isOwner } : m))
    );
  }, []);

  /**
   * Adds all pending members to the workspace
   * 
   * Makes parallel API calls to add all pending members,
   * refreshes workspace data, and shows success/error notifications.
   */
  const addAllMembers = useCallback(async () => {
    if (pendingMembers.length === 0) return;

    setIsAddingMembers(true);
    try {
      // Add all pending members in parallel
      const addPromises = pendingMembers.map((member) =>
        postAddUserToAWorkspace({
          getAccessToken,
          workspaceId,
          userId: member.id,
          isOwner: member.isOwner,
        })
      );

      await Promise.all(addPromises);

      // Refresh workspace data to get updated members list
      await refreshWorkspaces();

      // Close modal and reset state
      setIsModalOpen(false);
      resetInviteState();

      // Show success message
      const memberCount = pendingMembers.length;
      handleSuccess(
        `Successfully added ${memberCount} member${memberCount !== 1 ? 's' : ''} to workspace`
      );
    } catch (err) {
      const error = normalizeError(err, 'Failed to add members to workspace');
      const response = extractErrorResponse(err);

      console.error('Error adding members:', error);
      handleError(
        error instanceof ApiError ? error : error,
        'Failed to add members to workspace',
        response
      );
    } finally {
      setIsAddingMembers(false);
    }
  }, [pendingMembers, getAccessToken, workspaceId, refreshWorkspaces, resetInviteState, handleError, handleSuccess]);

  /**
   * Removes a user from the workspace
   * 
   * Shows confirmation dialog, makes API call, refreshes workspace data,
   * and shows success/error notifications.
   * 
   * @param userId - ID of the user to remove
   */
  const deleteUser = useCallback(
    async (userId: string) => {
      // Confirm deletion
      if (
        !window.confirm(
          'Are you sure you want to remove this user from the workspace?'
        )
      ) {
        return;
      }

      try {
        await deleteUserFromWorkspace({
          getAccessToken,
          workspaceId,
          userId,
        });

        // Refresh workspace data to reflect the change
        await refreshWorkspaces();

        handleSuccess('User removed from workspace successfully');
      } catch (err) {
        const error = normalizeError(err, 'Failed to remove user from workspace');
        const response = extractErrorResponse(err);

        console.error('Error deleting user from workspace:', error);
        handleError(
          error instanceof ApiError ? error : error,
          'Failed to remove user from workspace',
          response
        );
      }
    },
    [getAccessToken, workspaceId, refreshWorkspaces, handleError, handleSuccess]
  );

  /**
   * Makes a user an owner of the workspace.
   * Only workspace owners/admins should call this.
   *
   * @param userId - ID of the user to make owner
   */
  const makeUserOwner = useCallback(
    async (userId: string) => {
      try {
        await patchIsOwner({
          getAccessToken,
          workspaceId,
          userId,
          isOwner: true,
        });
        await refreshWorkspaces();
        handleSuccess('User is now a workspace owner');
      } catch (err) {
        const error = normalizeError(err, 'Failed to make user owner');
        const response = extractErrorResponse(err);
        console.error('Error making user owner:', error);
        handleError(
          error instanceof ApiError ? error : error,
          'Failed to make user owner',
          response
        );
      }
    },
    [getAccessToken, workspaceId, refreshWorkspaces, handleError, handleSuccess]
  );

  return {
    pendingMembers,
    isAddingMembers,
    isModalOpen,
    setIsModalOpen,
    addPendingMember,
    removePendingMember,
    togglePendingAdmin,
    addAllMembers,
    deleteUser,
    makeUserOwner,
    resetInviteState,
  };
};
